import React, { useMemo } from 'react';
import { AnalysisResult } from '../../contracts/scoring';
import { Fretboard } from '../../components/Fretboard';
import { ChromaVisualizer } from '../../components/ChromaVisualizer';
import { AdviceCard } from '../../components/SmartTrainer/AdviceCard';

type MetricCard = { label: string; value: string; hint?: string };

const safeNum = (n: any) => {
  const x = Number(n);
  return Number.isFinite(x) ? x : null;
};

const fmt = (n: any, decimals = 0) => {
  const x = safeNum(n);
  if (x === null) return '—';
  return decimals > 0 ? x.toFixed(decimals) : String(Math.round(x));
};

export const HarmonyTab: React.FC<{
  result: AnalysisResult;
  activeMeasure: number;
  onMeasureSelect: (idx: number) => void;
  beatsPerBar: number;
}> = ({ result, activeMeasure, onMeasureSelect, beatsPerBar }) => {
  const harmonyScore = result.session.metrics.harmony;

  // ✅ SAFE NORMALIZATION (évite "possibly undefined" partout)
  const harmony = useMemo(() => {
    const h = result.harmony;
    return {
      progression: Array.isArray(h?.progression) ? h!.progression : [],
      gridValidation: Array.isArray(h?.gridValidation) ? h!.gridValidation : [],
      chromaRef: Array.isArray(h?.chromaRef) ? h!.chromaRef : [],
      chromaStu: Array.isArray(h?.chromaStu) ? h!.chromaStu : [],
    };
  }, [result.harmony]);

  const diagnostics = result.feedback.diagnostics.filter(d => d.domain === 'HARMONY');
  // (tu ne l'utilises pas encore, on ne l'invente pas)
  const aiSummary = result.feedback.aiCoachSummary;

  // ✅ Mesures détectées (factuelles, sans inventer de champs)
  const metrics: MetricCard[] = useMemo(() => {
    const cards: MetricCard[] = [];

    const gv = harmony.gridValidation;
    const totalMeasures = gv.length;

    // ✅ gridValidation = ChordValidation[] : OK = isCorrect === true
    const okMeasures = gv.filter(v => (v as any)?.isCorrect === true).length;

    const progLen = harmony.progression.length || null;
    const chromaRefLen = harmony.chromaRef.length || null;
    const chromaStuLen = harmony.chromaStu.length || null;

    cards.push({
      label: 'Harmony score',
      value: `${fmt(harmonyScore)}%`,
      hint: 'Plus haut = mieux',
    });

    if (totalMeasures > 0) {
      cards.push({
        label: 'Validated measures',
        value: `${okMeasures}/${totalMeasures}`,
        hint: 'Mesures conformes à la grille',
      });
    }

    if (progLen !== null) {
      cards.push({
        label: 'Progression size',
        value: String(progLen),
        hint: "Nombre d’accords attendus",
      });
    }

    if (chromaRefLen !== null) {
      cards.push({
        label: 'Chroma ref frames',
        value: String(chromaRefLen),
        hint: 'Échantillons spectro (réf)',
      });
    }

    if (chromaStuLen !== null) {
      cards.push({
        label: 'Chroma stu frames',
        value: String(chromaStuLen),
        hint: 'Échantillons spectro (élève)',
      });
    }

    // Limite à 6 max pour rester premium
    return cards.slice(0, 6);
  }, [harmony, harmonyScore]);

  const scoreColor = harmonyScore > 80 ? 'text-emerald-400' : 'text-smart-gold';

  return (
    <div className="flex flex-col gap-10 animate-fade-in pb-12">
      {/* 1) SCORE HEADER & LOGIQUE DE CALCUL */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-4 bg-[#02080a] p-10 rounded-[48px] border border-white/10 shadow-2xl flex flex-col items-center text-center justify-center gap-6 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-smart-gold to-transparent"></div>
          <span className="text-[10px] font-black text-slate-500 uppercase tracking-[0.4em] italic">
            Harmonic Purity
          </span>
          <div className="flex items-baseline gap-2">
            <span className={`text-8xl font-black italic tracking-tighter ${scoreColor}`}>
              {harmonyScore}
            </span>
            <span className="text-xl font-bold text-slate-700">/100</span>
          </div>
          <div className="px-4 py-1 rounded-full bg-white/5 border border-white/10 text-[9px] font-black text-slate-400 uppercase tracking-widest">
            Indice de Résonance Utile
          </div>
        </div>

        <div className="lg:col-span-8 bg-black/40 p-8 rounded-[48px] border border-white/10 flex flex-col justify-center gap-6 shadow-inner relative overflow-hidden">
          <div className="absolute top-0 right-0 p-8 opacity-5 text-6xl font-black italic select-none">
            TRUTH
          </div>

          <div className="flex items-center gap-4 relative z-10">
            <div className="w-12 h-12 rounded-2xl bg-smart-gold/20 flex items-center justify-center text-2xl">
              ⚡
            </div>
            <h4 className="text-sm font-black text-white uppercase italic tracking-widest">
              Pourquoi ce score ?
            </h4>
          </div>

          <p className="text-sm text-slate-300 italic leading-relaxed pl-4 border-l-2 border-smart-gold/40 relative z-10">
            "L'IA ne fait pas une moyenne brute. Elle utilise une stratégie de{' '}
            <b>'Peak Analysis'</b> : elle identifie le moment où votre accord sonne le mieux dans la
            mesure et ignore les bruits de frottement inévitables pendant que vous changez de
            position. Votre score reflète la pureté de votre résonance stabilisée."
          </p>

          <div className="flex gap-3 relative z-10">
            <div className="px-4 py-2 rounded-xl bg-cyan-500/10 border border-cyan-500/20 text-[10px] font-black text-cyan-400 uppercase tracking-widest">
              🛡️ Transition Filter Active
            </div>
            <div className="px-4 py-2 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-[10px] font-black text-emerald-400 uppercase tracking-widest">
              ✓ Bass Note weighted x15
            </div>
          </div>
        </div>
      </div>

      {/* ✅ 2) MESURES DÉTECTÉES */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-7 bg-white/[0.02] border border-white/10 p-8 rounded-[48px] space-y-6">
          <h4 className="text-xs font-black text-slate-400 uppercase tracking-[0.3em]">
            Mesures détectées
          </h4>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {metrics.map((m, i) => (
              <div key={i} className="bg-black/40 border border-white/10 rounded-2xl p-5">
                <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">
                  {m.label}
                </div>
                <div className="text-2xl font-black italic text-white tabular-nums">{m.value}</div>
                {m.hint ? (
                  <div className="text-[9px] mt-2 italic text-slate-500">{m.hint}</div>
                ) : null}
              </div>
            ))}
          </div>
        </div>

        {/* ✅ 3) CONSEILS */}
        <div className="lg:col-span-5 flex flex-col gap-6">
          <div className="bg-white/[0.02] border border-white/10 p-8 rounded-[48px] space-y-6">
            <h4 className="text-xs font-black text-slate-400 uppercase tracking-[0.3em]">
              Conseils de correction
            </h4>
            <div className="space-y-4">
              {diagnostics.length > 0 ? (
                diagnostics.map((adv, i) => <AdviceCard key={i} advice={adv} isHighContrast={false} />)
              ) : (
                <div className="p-6 text-center opacity-40">
                  <p className="text-xs font-bold uppercase text-slate-500">Justesse Validée</p>
                </div>
              )}
            </div>
          </div>

          <div className="bg-smart-accent/5 border border-smart-accent/20 p-8 rounded-[40px] flex items-start gap-4 shadow-xl">
            <span className="text-2xl">💡</span>
            <div className="space-y-1">
              <span className="text-white font-black uppercase text-[10px] tracking-widest block mb-1">
                Secret du 100%
              </span>
              <p className="text-[11px] text-slate-400 font-medium italic leading-relaxed">
                Une seule corde qui "frise" ou qui est "étouffée" par la paume est détectée par l'IA
                comme du bruit. Pour un score parfait, assurez-vous que chaque note de l'accord
                chante librement.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* 4) INTERACTIVE TOOLS */}
      <div className="space-y-8">
        <Fretboard
          progression={harmony.progression}
          activeMeasure={activeMeasure}
          onMeasureSelect={onMeasureSelect}
          validation={harmony.gridValidation}
          bpm={result.session.bpm}
          beatsPerBar={beatsPerBar}
        />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-7">
            <ChromaVisualizer
              data={harmony.chromaRef}
              studentData={harmony.chromaStu}
              validation={harmony.gridValidation}
              activeMeasure={activeMeasure}
            />
          </div>

          <div className="lg:col-span-5">
            <div className="bg-black/40 p-8 rounded-[48px] border border-white/10 shadow-inner">
              <div className="text-[10px] font-black uppercase tracking-[0.4em] text-slate-500">
                Measure focus
              </div>
              <div className="mt-3 text-sm text-slate-300 italic leading-relaxed">
                Mesure active :{' '}
                <span className="text-white font-black not-italic">{activeMeasure + 1}</span>. Clique
                une mesure sur la grille pour voir l’accord attendu et la validation.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
